import { useId, type ReactNode } from 'react';
import { Button } from '../../components/Button';
import { Chip } from '../../components/Chip';
import { QrImage } from '../../components/QrImage';
import { AlertIcon, CheckIcon, DownloadIcon, StarIcon } from '../../components/icons';
import { formatDate } from './states';
import type { FreeRegens, QrVersion, VersionKind } from './types';
import { partnerQrImagePath } from './utils';

const KIND_LABEL: Record<VersionKind, string> = { art: 'QR Art', styled: 'Con estilo', classic: 'Clásico de respaldo' };

const COST_LABEL: Record<string, string> = {
  free: 'regeneración gratis',
  quota: 'descontada de la cuota',
  operator: 'pedida por el operador',
};

const NO_PRIMARY = 'Una versión que no escanea no puede ser principal: elige otra o la clásica.';

export interface VersionsGridProps {
  code: string;
  domain: string;
  versions: QrVersion[];
  orgId?: string;
  /** Regeneraciones gratis usadas de las disponibles (R2.1). */
  freeRegens?: FreeRegens | null;
  /** Versión en la que corre una acción; deshabilita sus botones. */
  busyId?: string | null;
  canManage: boolean;
  onDownload: (version: QrVersion) => void;
  onPrimary: (version: QrVersion) => void;
  onRetire: (version: QrVersion) => void;
  onReuse: (version: QrVersion) => void;
  /** Tarjeta de nueva versión, en borde punteado, cerrando la grilla. */
  newVersionForm?: ReactNode;
}

function versionTitle(v: QrVersion): string {
  if (v.label) return v.label;
  if (v.kind === 'classic') return 'Clásico del código';
  if (v.kind === 'styled') return 'Estilo del código';
  return 'QR Art sin nombre';
}

/** Medidor de cuota (design-spec §4): verde sobre gris, con el número al lado. Se llama `meter`, nunca `bar`. */
export function FreeRegensMeter({ freeRegens }: { freeRegens: FreeRegens }) {
  const labelId = useId();
  const pct = freeRegens.limit > 0 ? Math.min(100, Math.round((freeRegens.used / freeRegens.limit) * 100)) : 0;
  return (
    <div className="flex items-center gap-2 text-xs text-ink-muted">
      <span id={labelId}>Regeneraciones gratis</span>
      <span
        role="img"
        aria-labelledby={labelId}
        aria-label={`${freeRegens.used} de ${freeRegens.limit} regeneraciones gratis usadas`}
        className="h-2 w-[120px] overflow-hidden rounded-full bg-gray-200"
      >
        <span className="block h-full bg-success" style={{ width: `${pct}%` }} />
      </span>
      <strong className="tabular-nums text-ink">
        {freeRegens.used} de {freeRegens.limit} usadas
      </strong>
    </div>
  );
}

/** Tarjetas de versión del QR (R1): chips de rol y de lectura, metadatos y acciones. */
export function VersionsGrid({
  code,
  domain,
  versions,
  orgId,
  freeRegens,
  busyId,
  canManage,
  onDownload,
  onPrimary,
  onRetire,
  onReuse,
  newVersionForm,
}: VersionsGridProps) {
  const noPrimaryId = useId();
  const visible = versions.filter((v) => !v.retiredAt);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-base font-bold text-ink">Versiones de este QR</h2>
        {freeRegens && <FreeRegensMeter freeRegens={freeRegens} />}
      </div>
      <p className="text-sm text-ink-muted">
        Todas llevan al mismo link <span className="font-mono">{`${domain}/${code}`}</span>: cambiar de versión no cambia el destino, solo la imagen que
        imprimes.
      </p>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {visible.map((v) => {
          const generating = v.status === 'generando';
          const unreadable = v.scannable === false;
          const busy = busyId === v.id;
          return (
            <article
              key={v.id}
              className={`flex flex-col gap-2 rounded-lg border bg-white p-3 ${v.primary ? 'border-2 border-yellow' : 'border-line'}`}
            >
              {generating ? (
                <div className="aspect-square w-full animate-pulse rounded-md bg-green-soft motion-reduce:animate-none" role="status" aria-label={`Generando ${versionTitle(v)}`} />
              ) : v.imageUrl ? (
                <img
                  src={v.imageUrl}
                  alt={`${versionTitle(v)}: QR de ${code}`}
                  className="aspect-square w-full rounded-md border border-line object-contain"
                />
              ) : (
                <QrImage path={partnerQrImagePath({ code, domain }, { size: 512 })} orgId={orgId} alt={`${versionTitle(v)}: QR de ${code}`} />
              )}

              <div className="flex flex-wrap items-center gap-1.5">
                {v.primary ? (
                  <Chip tone="premium" dot={false}>
                    <StarIcon className="h-3.5 w-3.5" />
                    Principal
                  </Chip>
                ) : (
                  <Chip tone={v.kind === 'art' ? 'art' : 'neutral'} dot={false}>
                    {KIND_LABEL[v.kind]}
                  </Chip>
                )}
                {generating ? (
                  <Chip tone="art">Generando</Chip>
                ) : unreadable ? (
                  <Chip tone="error" dot={false}>
                    <AlertIcon className="h-3.5 w-3.5" />
                    No escanea
                  </Chip>
                ) : (
                  <Chip tone="success" dot={false}>
                    <CheckIcon className="h-3.5 w-3.5" />
                    Escanea
                  </Chip>
                )}
              </div>

              <div className="font-semibold text-ink">{versionTitle(v)}</div>
              <p className="text-xs tabular-nums text-ink-muted">
                {v.kind === 'classic'
                  ? 'Siempre disponible, se genera al descargar. No se puede retirar.'
                  : [KIND_LABEL[v.kind], formatDate(v.createdAt), v.createdBy, v.cost ? COST_LABEL[v.cost] : null].filter(Boolean).join(' · ')}
              </p>

              <div className="mt-auto flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" disabled={generating} onClick={() => onDownload(v)}>
                  <DownloadIcon />
                  Descargar
                </Button>
                {canManage && !v.primary && (
                  <Button
                    size="sm"
                    disabled={unreadable || generating || busy}
                    aria-describedby={unreadable ? `${noPrimaryId}-${v.id}` : undefined}
                    onClick={() => onPrimary(v)}
                  >
                    <StarIcon />
                    Marcar principal
                  </Button>
                )}
                {canManage && v.kind === 'art' && v.prompt && (
                  <Button size="sm" variant="ghost" onClick={() => onReuse(v)}>
                    Nueva con este prompt
                  </Button>
                )}
                {canManage && v.kind !== 'classic' && !v.primary && (
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => onRetire(v)}>
                    Retirar
                  </Button>
                )}
              </div>
              {unreadable && !v.primary && (
                <p id={`${noPrimaryId}-${v.id}`} className="text-xs text-ink-muted">
                  {NO_PRIMARY}
                </p>
              )}
            </article>
          );
        })}
        {newVersionForm}
      </div>
    </div>
  );
}
